import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { EVENT_TYPES_ARRAY } from 'src/consumer/constants'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import {
  getSupportAgentContactCapabilities,
  prepareSupportAgentEmail,
  sendSupportAgentWhatsappIntro,
  sendSupportAgentWhatsappMessage,
} from 'src/supportPortal/services/supportAgentContactService'

const DEFAULT_EVENT_TYPE_OPTIONS = EVENT_TYPES_ARRAY.filter((option) => option?.id).map((option) => ({
  value: option.id,
  label: option.title || option.id,
}))

function normalizeEventTypeOptions(options = []) {
  if (!Array.isArray(options) || options.length === 0) {
    return DEFAULT_EVENT_TYPE_OPTIONS
  }

  return options
    .map((option) => {
      if (typeof option === 'string') {
        return { value: option, label: option }
      }
      return {
        value: option?.value || option?.id || '',
        label: option?.label || option?.title || option?.value || option?.id || '',
      }
    })
    .filter((option) => option.value)
}

function DisabledButton({ title, children }) {
  return (
    <Tooltip title={title || ''} arrow>
      <span>{children}</span>
    </Tooltip>
  )
}

export default function SupportContactActions({
  patientId,
  practitionerId,
  bookedEventId,
  clinicBrandId,
  clinicId,
  targetRole,
  eventType,
  eventTypesCsv,
  eventTypeOptions,
  uiSurface = 'support-contact-actions',
  title = 'Contact Actions',
  compact = false,
  sx = {},
}) {
  const normalizedEventTypeOptions = useMemo(
    () => normalizeEventTypeOptions(eventTypeOptions),
    [eventTypeOptions],
  )
  const [selectedEventType, setSelectedEventType] = useState(
    eventType || normalizedEventTypeOptions[0]?.value || '',
  )
  const [loading, setLoading] = useState(false)
  const [capabilities, setCapabilities] = useState(null)
  const [capabilitiesError, setCapabilitiesError] = useState('')
  const [messageDialogOpen, setMessageDialogOpen] = useState(false)
  const [messageDraft, setMessageDraft] = useState('')
  const [messageSending, setMessageSending] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [introSending, setIntroSending] = useState(false)

  useEffect(() => {
    if (eventType) {
      setSelectedEventType(eventType)
      return
    }
    if (!selectedEventType && normalizedEventTypeOptions.length === 1) {
      setSelectedEventType(normalizedEventTypeOptions[0].value)
    }
  }, [eventType, normalizedEventTypeOptions, selectedEventType])

  const resolvedEventType = eventType || selectedEventType || ''
  const requiresEventTypeSelection = !eventTypesCsv && !resolvedEventType

  const context = useMemo(
    () => ({
      patientId,
      practitionerId,
      bookedEventId,
      clinicBrandId,
      clinicId,
      targetRole,
      eventType: resolvedEventType || undefined,
      eventTypesCsv: eventTypesCsv || undefined,
    }),
    [
      bookedEventId,
      clinicBrandId,
      clinicId,
      eventTypesCsv,
      patientId,
      practitionerId,
      resolvedEventType,
      targetRole,
    ],
  )

  useEffect(() => {
    let active = true

    async function loadCapabilities() {
      if (!patientId && !practitionerId) {
        setCapabilities(null)
        setCapabilitiesError('')
        return
      }
      if (requiresEventTypeSelection) {
        setCapabilities(null)
        setCapabilitiesError('')
        return
      }

      setLoading(true)
      const result = await getSupportAgentContactCapabilities(context)
      if (!active) {
        return
      }

      if (result?.ok) {
        setCapabilities(result)
        setCapabilitiesError('')
      } else {
        setCapabilities(null)
        setCapabilitiesError(result?.message || 'Unable to load contact capabilities.')
      }
      setLoading(false)
    }

    loadCapabilities()

    return () => {
      active = false
    }
  }, [context, patientId, practitionerId, requiresEventTypeSelection])

  async function refreshCapabilities() {
    if (!patientId && !practitionerId) {
      return
    }
    const result = await getSupportAgentContactCapabilities(context)
    if (result?.ok) {
      setCapabilities(result)
      setCapabilitiesError('')
    } else {
      setCapabilities(null)
      setCapabilitiesError(result?.message || 'Unable to load contact capabilities.')
    }
  }

  async function handleSendIntro() {
    setIntroSending(true)
    const result = await sendSupportAgentWhatsappIntro(context)
    setIntroSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp intro.')
      return
    }

    toast.success('WhatsApp intro sent.')
    refreshCapabilities()
  }

  async function handleSendMessage() {
    if (!messageDraft.trim()) {
      toast.error('Enter a message first.')
      return
    }

    setMessageSending(true)
    const result = await sendSupportAgentWhatsappMessage(context, messageDraft.trim())
    setMessageSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp message.')
      return
    }

    toast.success('WhatsApp message sent.')
    setMessageDraft('')
    setMessageDialogOpen(false)
    refreshCapabilities()
  }

  async function handlePrepareEmail() {
    setEmailLoading(true)
    const result = await prepareSupportAgentEmail(context)
    setEmailLoading(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to prepare the email draft.')
      return
    }

    const mailtoUri = result?.data?.mailtoUri
    if (!mailtoUri) {
      toast.error('Email draft URL was not returned.')
      return
    }
    window.location.href = mailtoUri
  }

  const channels = capabilities?.channels || {}
  const phoneChannel = channels?.phone || {}
  const whatsappChannel = channels?.whatsapp || {}
  const emailChannel = channels?.email || {}

  const disableAllActions = loading || requiresEventTypeSelection
  const selectionMessage = requiresEventTypeSelection
    ? 'Choose an event category first.'
    : ''

  return (
    <Box
      sx={{
        p: compact ? 1.25 : 1.5,
        borderRadius: 2,
        border: '1px solid rgba(59, 130, 246, 0.14)',
        background:
          'linear-gradient(135deg, rgba(239, 246, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
        ...sx,
      }}
    >
      {title ? (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: '#1d4ed8', mb: compact ? 1 : 1.25 }}
        >
          {title}
        </Typography>
      ) : null}

      {!eventType && !eventTypesCsv ? (
        <TextField
          select
          fullWidth
          size="small"
          label="Support Category"
          value={selectedEventType}
          onChange={(event) => setSelectedEventType(event.target.value)}
          sx={{ mb: compact ? 1 : 1.25 }}
        >
          {normalizedEventTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: compact ? 1 : 1.25 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Checking allowed contact modes...
          </Typography>
        </Box>
      ) : null}

      {capabilitiesError ? (
        <Alert severity="warning" sx={{ mb: compact ? 1 : 1.25 }}>
          {capabilitiesError}
        </Alert>
      ) : null}

      {!capabilitiesError && selectionMessage ? (
        <Alert severity="info" sx={{ mb: compact ? 1 : 1.25 }}>
          {selectionMessage}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {phoneChannel?.enabled && !disableAllActions ? (
          <PortalVoiceCallButton
            actorRole="support-agent"
            patientId={patientId}
            practitionerId={practitionerId}
            bookedEventId={bookedEventId}
            clinicBrandId={clinicBrandId}
            clinicId={clinicId}
            targetRole={targetRole}
            eventType={resolvedEventType || undefined}
            eventTypesCsv={eventTypesCsv || undefined}
            uiSurface={uiSurface}
            allowedDestinationTypes={[phoneChannel?.destinationType]}
            autoStartWhenSingleRoute
            buttonLabel="Call"
            size="small"
          />
        ) : (
          <DisabledButton title={selectionMessage || phoneChannel?.reason}>
            <Button variant="contained" size="small" disabled>
              Call
            </Button>
          </DisabledButton>
        )}

        {whatsappChannel?.enabled && !disableAllActions ? (
          whatsappChannel?.warmWindowOpen ? (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<WhatsAppIcon />}
              onClick={() => setMessageDialogOpen(true)}
            >
              WhatsApp
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={introSending ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon />}
              onClick={handleSendIntro}
              disabled={introSending}
            >
              Send Intro
            </Button>
          )
        ) : (
          <DisabledButton title={selectionMessage || whatsappChannel?.reason}>
            <Button variant="outlined" size="small" color="success" disabled startIcon={<WhatsAppIcon />}>
              WhatsApp
            </Button>
          </DisabledButton>
        )}

        {emailChannel?.enabled && !disableAllActions ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={emailLoading ? <CircularProgress size={14} color="inherit" /> : <EmailRoundedIcon />}
            onClick={handlePrepareEmail}
            disabled={emailLoading}
          >
            Email
          </Button>
        ) : (
          <DisabledButton title={selectionMessage || emailChannel?.reason}>
            <Button variant="outlined" size="small" disabled startIcon={<EmailRoundedIcon />}>
              Email
            </Button>
          </DisabledButton>
        )}
      </Stack>

      {phoneChannel?.outcallNumber ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Patient call alerts use outcall number {phoneChannel.outcallNumber}.
        </Typography>
      ) : null}

      <Dialog
        open={messageDialogOpen}
        onClose={() => {
          if (!messageSending) {
            setMessageDialogOpen(false)
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send WhatsApp Message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            margin="dense"
            label="Message"
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            placeholder="Type the message you want to send..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)} disabled={messageSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={messageSending}
            startIcon={messageSending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
